/**
 * Registration-abandonment tracker.
 *
 * Owns the answer to "did this visitor walk out of the sign-up flow?". The auth
 * screens only report what happened to them; the decision to fire lives here, so
 * both entry points (the browser modal via LoginReg and the PWA inline screen)
 * behave identically.
 *
 * It fires only when ALL of these hold:
 *   - the sign-up form was open for at least trigger.config.delay_seconds
 *   - the visitor actually started (the email field had content)
 *   - the flow was not completed
 *   - the visitor left it (closed the form, or navigated away from the page)
 *
 * The pop-up itself is requested via the shared bus; PopupHost applies the
 * frequency cap and renders. When no registration_abandonment pop-up is enabled
 * the tracker never arms, so this is inert on a brand that has not enabled it.
 */

import { POPUP_TYPES, loadPopup, requestPopup, triggerNumber } from "utils/popups";

const state = {
  configured: false, // an enabled registration_abandonment pop-up exists
  delayMs: 0,
  openedAt: 0,
  started: false, // the visitor typed something into the email field
  suspended: false, // handed off to another screen of the SAME flow (OTP)
  fired: false,
};

let configPromise = null;

/** Resolve the config once per page load; the tracker stays inert without it. */
function ensureConfig() {
  if (configPromise) return configPromise;
  configPromise = loadPopup(POPUP_TYPES.REGISTRATION_ABANDONMENT).then((popup) => {
    state.configured = Boolean(popup);
    state.delayMs = popup ? triggerNumber(popup, "delay_seconds", 0) * 1000 : 0;
    return popup;
  });
  return configPromise;
}

function reset() {
  state.openedAt = 0;
  state.started = false;
  state.suspended = false;
}

/** Armed = a real, unfinished sign-up attempt that has met the delay. */
function isArmed() {
  return (
    state.configured &&
    !state.fired &&
    !state.suspended &&
    state.started &&
    state.openedAt > 0 &&
    Date.now() - state.openedAt >= state.delayMs
  );
}

function fire() {
  state.fired = true;
  reset();
  requestPopup(POPUP_TYPES.REGISTRATION_ABANDONMENT);
}

/** The sign-up form became visible. Re-entry keeps the original open time. */
export function beginSignupFlow() {
  ensureConfig();
  state.suspended = false;
  if (!state.openedAt) state.openedAt = Date.now();
}

/** The email field changed. `hasContent` false does not disarm an earlier start. */
export function noteSignupInput(hasContent) {
  if (hasContent) state.started = true;
}

/**
 * Moving to another screen of the same flow (email step -> OTP step). Not an
 * abandonment: the form unmounting behind it must not fire.
 */
export function suspendSignupFlow() {
  state.suspended = true;
}

/**
 * Back from that other screen onto the form. Needed for the PWA inline path,
 * where the form never unmounts and so would otherwise stay suspended forever.
 */
export function resumeSignupFlow() {
  if (state.openedAt) state.suspended = false;
}

/** The visitor finished signing up — nothing to win back. */
export function completeSignupFlow() {
  state.fired = true;
  reset();
}

/** The sign-up form went away. Fires only when the conditions above all hold. */
export function endSignupFlow() {
  if (state.suspended) return;
  if (isArmed()) {
    fire();
    return;
  }
  reset();
}

/**
 * Leaving the page mid-sign-up. Registered once, globally: the pop-up can only
 * be painted if the unload is cancelled, which is the same best-effort deal the
 * exit-intent popup already lives with.
 */
let unloadBound = false;

export function bindSignupAbandonmentUnload() {
  if (unloadBound) return () => {};
  unloadBound = true;
  const handler = (e) => {
    if (!isArmed()) return;
    fire();
    e.preventDefault();
    e.returnValue = "";
  };
  window.addEventListener("beforeunload", handler);
  return () => {
    window.removeEventListener("beforeunload", handler);
    unloadBound = false;
  };
}

/** Test/diagnostic helper — the live code never needs it. */
export function getSignupAbandonmentState() {
  return { ...state };
}
